import React, { useEffect } from "react"
import { View, ScrollView } from "react-native"
import { IconButton } from "react-native-paper"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { IconImageM } from "@common/components/icons/IconsM"

export const getUpdateAllocationHoursFn =
    ({ selectedSubjects, hoursPerWeek, allocation, setAllocation }) =>
    (changedSubjectId, newHours) => {
        const otherSubjects = selectedSubjects.filter(
            (id) => id !== changedSubjectId
        )

        if (otherSubjects.length === 0) {
            // Only one subject, set to max hours
            setAllocation({ [changedSubjectId]: hoursPerWeek })
            return
        }

        // Calculate remaining hours for other subjects
        const remainingHours = hoursPerWeek - newHours

        if (remainingHours < otherSubjects.length) {
            // Not enough hours to give at least 1h to each other subject
            // Reduce the changed subject and redistribute
            const maxHours = hoursPerWeek - otherSubjects.length
            const adjustedHours = Math.max(1, Math.min(newHours, maxHours))
            const actualRemaining = hoursPerWeek - adjustedHours

            const newAllocation = {
                ...allocation,
                [changedSubjectId]: adjustedHours,
            }

            // Give 1h to each other subject, distribute extra
            const baseHours = Math.floor(actualRemaining / otherSubjects.length)
            const extraHours = actualRemaining % otherSubjects.length

            otherSubjects.forEach((subjectId, index) => {
                newAllocation[subjectId] =
                    baseHours + (index < extraHours ? 1 : 0)
            })

            setAllocation(newAllocation)
        } else {
            // Distribute remaining hours among other subjects
            const newAllocation = {
                ...allocation,
                [changedSubjectId]: newHours,
            }

            // Proportional redistribution based on current allocation
            const currentOtherTotal = otherSubjects.reduce(
                (sum, id) => sum + (allocation[id] || 1),
                0
            )

            if (currentOtherTotal === 0) {
                // Equal redistribution if no existing allocation
                const baseHours = Math.floor(
                    remainingHours / otherSubjects.length
                )
                const extraHours = remainingHours % otherSubjects.length

                otherSubjects.forEach((subjectId, index) => {
                    newAllocation[subjectId] =
                        baseHours + (index < extraHours ? 1 : 0)
                })
            } else {
                // Proportional redistribution
                let distributed = 0
                otherSubjects.forEach((subjectId, index) => {
                    if (index === otherSubjects.length - 1) {
                        // Last subject gets remaining hours to ensure exact total
                        newAllocation[subjectId] = remainingHours - distributed
                    } else {
                        const proportion =
                            (allocation[subjectId] || 1) / currentOtherTotal
                        const proportionalHours = Math.max(
                            1,
                            Math.round(remainingHours * proportion)
                        )
                        newAllocation[subjectId] = proportionalHours
                        distributed += proportionalHours
                    }
                })
            }

            setAllocation(newAllocation)
        }
    }

const HourAllocationStep = ({
    field,
    setFormValues,
    hoursPerWeek,
    selectedSubjects,
    schoolSubjects = [],
    scrollViewStyle,
    generateDefaultSubjectHoursAllocation,
}) => {
    const allocation = field.value || {}
    const setAllocation = (newAllocation) => {
        field.onChange?.(newAllocation)
        setFormValues((prev) => ({
            ...prev,
            [field.name]: newAllocation,
        }))
    }

    const updateHours = getUpdateAllocationHoursFn({
        selectedSubjects,
        hoursPerWeek,
        allocation,
        setAllocation,
    })

    // Handle when selectedSubjects change (user went back and changed selection)
    // Also handles initial setup if subjects weren't available during useState init
    useEffect(() => {
        if (!selectedSubjects?.length) {
            setAllocation({})
            return
        }

        // Check if current allocation is still valid for the new subject selection
        const currentSubjectIds = Object.keys(allocation)
        const selectedSubjectIds = selectedSubjects.map(String)

        // If subjects changed or if we have no current allocation, regenerate
        const subjectsChanged =
            currentSubjectIds.length !== selectedSubjectIds.length ||
            !currentSubjectIds.every((id) => selectedSubjectIds.includes(id)) ||
            !selectedSubjectIds.every((id) => currentSubjectIds.includes(id))

        const hasNoAllocation = Object.keys(allocation).length === 0

        if (subjectsChanged || hasNoAllocation) {
            const newAllocation = generateDefaultSubjectHoursAllocation(
                selectedSubjects,
                hoursPerWeek
            )
            setAllocation(newAllocation)
        }
    }, [selectedSubjects, hoursPerWeek, generateDefaultSubjectHoursAllocation])

    // Auto-balance hours when one subject changes

    const getSubjectInfo = (subjectId) => {
        return schoolSubjects.find((s) => s.id === subjectId)
    }

    const totalAllocated = Object.values(allocation).reduce(
        (sum, hours) => sum + (hours || 0),
        0
    )

    if (!selectedSubjects?.length) {
        return (
            <View
                style={{
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <TextM color={theme.colors.light}>
                    Aucune matière sélectionnée
                </TextM>
            </View>
        )
    }

    return (
        <ScrollView
            style={{ width: "100%" }}
            contentContainerStyle={{
                paddingHorizontal: 10,
                paddingBottom: 100,
                ...scrollViewStyle,
            }}
        >
            {selectedSubjects.map((subjectId) => {
                const subject = getSubjectInfo(subjectId)
                const hours = allocation[subjectId] || 0
                const maxHours = hoursPerWeek - (selectedSubjects.length - 1) // At least 1h for others

                return (
                    <View
                        key={subjectId}
                        style={{
                            backgroundColor: theme.colors.white,
                            borderWidth: 1,
                            borderColor: theme.colors.lightMore,
                            borderRadius: 12,
                            padding: 16,
                            marginBottom: 12,
                            flexDirection: "row",
                            alignItems: "center",
                        }}
                    >
                        {/* Subject Info */}
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                flex: 1,
                            }}
                        >
                            {subject?.icon && (
                                <View
                                    style={{
                                        marginRight: 12,
                                        padding: 8,
                                        borderRadius: 20,
                                        borderWidth: 1,
                                        borderColor: theme.colors.lightMore,
                                        backgroundColor: subject.color
                                            ? subject.color + "15"
                                            : theme.colors.lighter,
                                    }}
                                >
                                    <IconImageM uri={subject.icon} width={24} />
                                </View>
                            )}
                            <View style={{ flex: 1 }}>
                                <TextM fontWeight="Medium" fontSize={16}>
                                    {subject?.name || `Matière ${subjectId}`}
                                </TextM>
                                <TextM
                                    fontSize={12}
                                    color={theme.colors.grey}
                                    style={{ marginTop: 2 }}
                                >
                                    {hours}h/semaine
                                </TextM>
                            </View>
                        </View>

                        {/* Hour Controls */}
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                            }}
                        >
                            <IconButton
                                icon="minus"
                                size={20}
                                disabled={hours <= 1}
                                onPress={() =>
                                    updateHours(
                                        subjectId,
                                        Math.max(1, hours - 1)
                                    )
                                }
                                style={{
                                    backgroundColor:
                                        hours <= 1
                                            ? theme.colors.lighter
                                            : theme.colors.primaryLighter,
                                    margin: 0,
                                    width: 36,
                                    height: 36,
                                }}
                                iconColor={
                                    hours <= 1
                                        ? theme.colors.light
                                        : theme.colors.primary
                                }
                            />

                            <View
                                style={{
                                    backgroundColor:
                                        theme.colors.primaryLighter,
                                    borderColor: theme.colors.primary,
                                    borderWidth: 1,
                                    borderRadius: 8,
                                    paddingVertical: 8,
                                    paddingHorizontal: 16,
                                    marginHorizontal: 8,
                                    minWidth: 50,
                                    alignItems: "center",
                                }}
                            >
                                <TextM
                                    fontWeight="Bold"
                                    fontSize={18}
                                    color={theme.colors.primary}
                                >
                                    {hours}
                                </TextM>
                            </View>

                            <IconButton
                                icon="plus"
                                size={20}
                                disabled={hours >= maxHours}
                                onPress={() =>
                                    updateHours(
                                        subjectId,
                                        Math.min(maxHours, hours + 1)
                                    )
                                }
                                style={{
                                    backgroundColor:
                                        hours >= maxHours
                                            ? theme.colors.lighter
                                            : theme.colors.primaryLighter,
                                    margin: 0,
                                    width: 36,
                                    height: 36,
                                }}
                                iconColor={
                                    hours >= maxHours
                                        ? theme.colors.light
                                        : theme.colors.primary
                                }
                            />
                        </View>
                    </View>
                )
            })}

            {/* Total Display */}
            <View
                style={{
                    backgroundColor:
                        totalAllocated === hoursPerWeek
                            ? theme.colors.successLight
                            : theme.colors.errorLight,
                    borderWidth: 1,
                    borderColor:
                        totalAllocated === hoursPerWeek
                            ? theme.colors.success
                            : theme.colors.error,
                    borderRadius: 12,
                    padding: 16,
                    marginTop: 8,
                    alignItems: "center",
                }}
            >
                <TextM
                    fontWeight="Bold"
                    fontSize={16}
                    color={
                        totalAllocated === hoursPerWeek
                            ? theme.colors.success
                            : theme.colors.error
                    }
                >
                    Total: {totalAllocated}/{hoursPerWeek} heures
                </TextM>
                {totalAllocated !== hoursPerWeek && (
                    <TextM
                        fontSize={12}
                        color={theme.colors.error}
                        style={{ marginTop: 4, textAlign: "center" }}
                    >
                        Ajustement automatique en cours...
                    </TextM>
                )}
            </View>
        </ScrollView>
    )
}

export default HourAllocationStep
